import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { animated } from 'react-spring';

import { Product } from '../interfaces';
import { Button, MoreInformationButton } from './Buttons';
import { useAppData, useIntl } from '../providers';
import ProductCharacteristics from './ProductCharacteristics';
import { useCallback } from 'react';

const useStyles = createUseStyles({
    product: {
        padding: '2rem',
        backgroundColor: '#e6e6e6',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flex: 0,
        paddingRight: '100px',
        '& h3': {
            color: 'var(--primary-color)',
            fontSize: '1em',
            fontWeight: 'bold',
            lineHeight: '1.5em',
            cursor: 'pointer',
            marginBottom: '0',

            '@media (min-width: 1520px)': {
                lineHeight: '1.6em',
            },
        },
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        flex: 0,
        margin: '1rem -1rem',
        '& > *': {
            flex: 1,
            margin: '0 1rem',
        },
    },
    logo: {
        '& img': {
            maxWidth: '100px',
            maxHeight: '100px',
            height: 'auto',
        },
        '@media (min-width: 640px)': {
            position: 'absolute',
            right: '1rem',
            top: '1rem',
        },
    },
    buttonShowAll: {
        marginTop: '10px'
    }
});

interface ProductProps {
    product: Product;
    style?: object;
}

const ProductCard = ({ product, style = {} }: ProductProps, ref?: React.Ref<any>) => {
    const classes = useStyles();
    const { translate } = useIntl();
    const {
        selectProduct,
        selectedCategory,
        viewProductsForBranch,
        parentCategories
    } = useAppData();

    const handleOnShowMoreInformation = useCallback(() => {
        selectProduct(product);
    }, [selectProduct, product]);

    const handleOrderButtonClick = useCallback(() => {
        selectProduct(product, 'order');
    }, [selectProduct, product]);

    const handleViewBranch = useCallback(() => {
        viewProductsForBranch(parentCategories[0]);
    }, [parentCategories]);

    const isRootCategory = () => {
        return selectedCategory.parentPath ? false : true;
    }

    return (
        <animated.article className={classes.product} style={style} ref={ref}>
            <header className={classes.header}>
                <h3 onClick={handleOnShowMoreInformation}>{product.label}</h3>
            </header>
            {product.logo && (
                <div className={classes.logo}>
                    <img src={product.logo} alt={product.logoAlternativeText} />
                </div>
            )}
            <ProductCharacteristics product={product} />
            <footer className={classes.actions}>
                <span>
                    <MoreInformationButton onClick={handleOnShowMoreInformation} />
                </span>
                <span>
                    <Button
                        onClick={handleOrderButtonClick}
                        label={
                            product.isNotAvailable ?
                                translate('productCard.action.isNotAvailable', 'Not available') :
                                translate('productCard.action.order', 'order')
                        }
                        disabled={product.isNotAvailable}
                    />

                    {product.isNotAvailable && !isRootCategory() && (
                        <Button
                            className={classes.buttonShowAll}
                            onClick={handleViewBranch}
                            label={translate('productCard.action.alternativeProducts', 'Altenative products')}
                        />
                    )}
                </span>
            </footer>
        </animated.article>
    );
};

export default React.forwardRef(ProductCard);
